
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useState } from "react";
import { Database } from "@/integrations/supabase/types";
import { fetchUsers } from "@/data/services/userOperations";

type AppRole = Database["public"]["Enums"]["app_role"];

// Define the ProfileType interface based on the profiles table structure
interface ProfileType {
  id: string;
  email: string | null;
  display_name: string | null;
  is_verified: boolean | null;
  tos_accepted: boolean | null;
  phone: string | null;
  address: string | null;
  location: string | null;
}

export interface UserProfile {
  id: string;
  email: string | null;
  display_name: string | null;
  role: AppRole;
  is_verified: boolean;
  tos_accepted: boolean;
  is_profile_complete?: boolean;
}

export const useUserData = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [debugInfo, setDebugInfo] = useState<any>(null);
  const [isCreatingMissingProfiles, setIsCreatingMissingProfiles] = useState(false);

  const { data: users, isLoading, refetch } = useQuery({
    queryKey: ["admin-users"],
    queryFn: async () => {
      try {
        const profiles = await fetchUsers();
        
        const { data: userRoles, error: rolesError } = await supabase
          .from("user_roles")
          .select("*");
        
        if (rolesError) {
          console.error("Error fetching user roles:", rolesError);
          throw rolesError;
        }
        
        const roleMap = new Map();
        userRoles?.forEach(userRole => {
          roleMap.set(userRole.user_id, userRole.role);
        });
        
        const combinedUsers = profiles.map((profile: any) => {
          const role = roleMap.get(profile.id) || "user";
          
          return {
            id: profile.id,
            email: profile.email || null,
            display_name: profile.display_name,
            role: role,
            is_verified: profile.is_verified || false,
            tos_accepted: profile.tos_accepted || false,
            is_profile_complete: profile.is_profile_complete || false
          };
        });
        
        return combinedUsers as UserProfile[];
      } catch (error) {
        console.error("Error in fetchUsers:", error);
        return [];
      }
    },
    staleTime: 60000,
  });

  const ensureUserProfiles = async (authUsers: any[], existingProfiles: ProfileType[]) => {
    if (!authUsers || !existingProfiles) return [];
    
    setIsCreatingMissingProfiles(true);
    const profilesMap = new Map();
    existingProfiles.forEach(profile => {
      profilesMap.set(profile.id, profile);
    });
    
    const createdProfiles = [];
    
    for (const authUser of authUsers) {
      if (!profilesMap.has(authUser.id)) {
        console.log(`Creating missing profile for ${authUser.email}`);
        
        try {
          const displayName = 
            authUser.user_metadata?.display_name || 
            (authUser.email ? authUser.email.split('@')[0] : 'User');
          
          const { data, error } = await supabase.rpc('create_or_update_profile', {
            user_id: authUser.id,
            user_email: authUser.email,
            user_display_name: displayName
          });
          
          if (error) {
            console.error(`Error creating profile for ${authUser.id}:`, error);
          } else {
            console.log(`Successfully created profile:`, data);
            if (data) createdProfiles.push(data[0]);
          }
        } catch (error) {
          console.error(`Error calling create_or_update_profile for ${authUser.id}:`, error);
        }
      }
    }
    
    setIsCreatingMissingProfiles(false);
    return createdProfiles;
  };

  const fixMissingProfileData = async (profiles: ProfileType[], authData: any[]) => {
    if (!profiles || !authData) return;
    
    for (const authUser of authData) {
      const profile = profiles.find(p => p.id === authUser.id);
      
      if (profile) {
        if (!profile.email || !profile.display_name) {
          const email = authUser.email;
          
          try {
            const { data, error } = await supabase.rpc('create_or_update_profile', {
              user_id: profile.id,
              user_email: email || null,
              user_display_name: email ? email.split('@')[0] : 'User'
            });
            
            console.log("Fix profile update result:", { data, error });
          } catch (error) {
            console.error(`Error fixing profile for ${profile.id}:`, error);
          }
        }
      }
    }
  };

  const updateUserRoleMutation = useMutation({
    mutationFn: async ({ userId, role }: { userId: string; role: AppRole }) => {
      const { data: existingRole } = await supabase
        .from("user_roles")
        .select("*")
        .eq("user_id", userId)
        .maybeSingle();

      if (existingRole) {
        const { error } = await supabase
          .from("user_roles")
          .update({ role })
          .eq("user_id", userId);
        
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from("user_roles")
          .insert({ user_id: userId, role });
        
        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-users"] });
      toast({ title: "Success", description: "User role updated successfully" });
    },
    onError: (error: Error) => {
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message,
      });
    },
  });

  const updateProfileMutation = useMutation({
    mutationFn: async ({ 
      userId, 
      displayName, 
      email,
      tosAccepted
    }: { 
      userId: string; 
      displayName: string; 
      email?: string;
      tosAccepted?: boolean;
    }) => {
      console.log("Starting profile update with data:", { 
        userId, 
        displayName, 
        email,
        tosAccepted
      });
      
      const updateData: any = {
        display_name: displayName || null
      };
      
      if (email !== undefined) updateData.email = email;
      if (tosAccepted !== undefined) updateData.tos_accepted = tosAccepted;
      
      const { data, error } = await supabase
        .from('profiles')
        .update(updateData)
        .eq('id', userId);
      
      console.log("Profile update result:", { data, error });
      
      if (error) throw error;
      
      setDebugInfo({ 
        method: "update", 
        result: data, 
        params: { 
          userId, 
          displayName, 
          email,
          tosAccepted
        } 
      });
      
      return data;
    },
    onSuccess: (data) => {
      console.log("Update profile mutation succeeded with data:", data);
      queryClient.invalidateQueries({ queryKey: ["admin-users"] });
      toast({ 
        title: "Success", 
        description: "User profile updated successfully." 
      });
    },
    onError: (error: Error) => {
      console.error("Update profile mutation failed with error:", error);
      toast({
        variant: "destructive",
        title: "Error updating profile",
        description: error.message,
      });
    },
  });

  return {
    users,
    isLoading,
    refetch,
    updateUserRoleMutation,
    updateProfileMutation,
    ensureUserProfiles,
    fixMissingProfileData,
    debugInfo,
    isCreatingMissingProfiles
  };
};
